/**
 * Pure utility functions for friendship needs assessment calculations.
 * No React dependencies - these are pure JavaScript functions.
 */

/**
 * Get target close friends count from assessment answers
 * @param {Object} answersObj - Assessment answers object
 * @returns {Object|null} Object with min, max, and target, or null
 */
export const getTargetCloseFriends = (answersObj) => {
  const quantityAnswer = answersObj['friendship-quantity'];
  if (!quantityAnswer) return null;

  const quantityMap = {
    few: { min: 1, max: 3 },
    moderate: { min: 4, max: 6 },
    many: { min: 7, max: 10 }
  };

  const range = quantityMap[quantityAnswer.value];
  if (!range) return null;

  const target = Math.round((range.min + range.max) / 2);
  return { ...range, target };
};

/**
 * Get emotional depth preference from assessment answers
 * @param {Object} answersObj - Assessment answers object
 * @returns {Object|null} Object with level and label, or null
 */
export const getEmotionalDepthPreference = (answersObj) => {
  const depthAnswer = answersObj['connection-depth'];
  if (!depthAnswer) return null;

  return {
    level: depthAnswer.value, // 'deep' | 'balanced' | 'casual'
    label: depthAnswer.label
  };
};

/**
 * Check if desired quantity is realistic given time investment and social energy
 * @param {Object} answersObj - Assessment answers object
 * @param {number} targetCount - Target number of close friends
 * @returns {boolean} True if realistic, false otherwise
 */
export const isQuantityRealistic = (answersObj, targetCount) => {
  if (!targetCount) return true;
  const time = answersObj['time-investment']?.value; // high | moderate | limited
  const energy = answersObj['social-energy']?.value; // high | moderate | low

  if (targetCount >= 7 && (time === 'limited' || energy === 'low')) return false;
  if (targetCount >= 5 && time === 'limited') return false;
  return true;
};

/**
 * Calculate needs profile from assessment answers
 * @param {Array} questions - Array of assessment questions
 * @param {Object} answers - User's answers
 * @param {Function} t - Translation function
 * @returns {Object} Needs profile object
 */
export const calculateNeedsProfile = (questions, answers, t) => {
  const scores = {
    emotional: 0,
    social: 0,
    frequency: 0,
    support: 0,
    practical: 0,
    communication: 0,
    growth: 0,
    fun: 0,
    depth: 0,
    time: 0,
    repair: 0
  };

  // Track how many questions contribute to each category
  const categoryCounts = {
    emotional: 0,
    social: 0,
    frequency: 0,
    support: 0,
    practical: 0,
    communication: 0,
    growth: 0,
    fun: 0,
    depth: 0,
    time: 0,
    repair: 0
  };

  let totalQuestions = 0;
  questions.forEach(question => {
    const answer = answers[question.id];
    if (answer && answer.score) {
      totalQuestions++;
      Object.keys(answer.score).forEach(category => {
        if (scores.hasOwnProperty(category)) {
          scores[category] += answer.score[category];
          categoryCounts[category]++;
        }
      });
    }
  });

  // Normalize scores to 0-5 scale
  // Each category can get max 5 points per question, so we normalize based on
  // the number of questions that contribute to that category
  const normalizedScores = {};
  Object.keys(scores).forEach(key => {
    const count = categoryCounts[key] || 1; // Avoid division by zero
    // Maximum possible score for this category = count * 5
    // Normalize: (actual score / max possible) * 5
    const maxPossible = count * 5;
    normalizedScores[key] = maxPossible > 0 
      ? Math.round((scores[key] / maxPossible) * 5 * 10) / 10
      : 0;
    // Cap at 5.0
    normalizedScores[key] = Math.min(normalizedScores[key], 5.0);
  });

  // Determine primary needs (top 3 categories)
  const sortedNeeds = Object.entries(normalizedScores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([key, value]) => ({ need: key, score: value }));

  // Extract preferences
  const preferences = {
    emotionalDepth: getEmotionalDepthPreference(answers),
    closeFriendCount: getTargetCloseFriends(answers)
  };

  const quantityTarget = preferences.closeFriendCount?.target || null;
  const quantityIsRealistic = isQuantityRealistic(answers, quantityTarget);

  return {
    scores: normalizedScores,
    primaryNeeds: sortedNeeds,
    answers: answers,
    preferences,
    quantityIsRealistic,
    completedAt: new Date().toISOString()
  };
};

/**
 * Generate friendship goals from profile
 * @param {Object} profile - Needs profile object
 * @param {Function} t - Translation function
 * @returns {Array} Array of auto-generated goal objects
 */
export const generateFriendshipGoalsFromProfile = (profile, t) => {
  if (!profile) return [];

  const prefs = profile.preferences || {};
  const depth = prefs.emotionalDepth;
  const qty = prefs.closeFriendCount;

  const autoGoals = [];

  if (qty) {
    autoGoals.push({
      id: 'goal-close-friends-count',
      type: 'quantitative',
      metric: 'closeFriendsCount',
      target: qty.target,
      text: t('autoGoalCloseFriendsCount', { target: qty.target }),
      completed: false,
      createdAt: new Date().toISOString()
    });
  }

  if (depth) {
    autoGoals.push({
      id: 'goal-emotional-depth',
      type: 'qualitative',
      metric: 'emotionalDepth',
      target: depth.level,
      text: t('autoGoalEmotionalDepth', { depth: depth.label.toLowerCase() }),
      completed: false,
      createdAt: new Date().toISOString()
    });
  }

  return autoGoals;
};
